/**
 * Strategic Reports Page - retention/dropout, enrollment trends, teacher
 * workload, fee collection forecasting, cross-campus comparison,
 * proactive attendance-risk alerting, a custom report builder, and
 * scheduled/emailed reports. Sits alongside the existing
 * AnalyticsDashboardPage (per-student/class learning analytics) and
 * ReportsPage (canned attendance/fee/academic/enrollment reports) rather
 * than replacing either.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import strategicReportsService from '../services/strategicReportsService';
import schoolsService from '../services/schoolsService';
import { CircleNotch, DownloadSimple } from '@phosphor-icons/react';
import './StrategicReportsPage.css';

const TABS = [
  { key: 'retention', label: 'Retention & Enrollment' },
  { key: 'workload', label: 'Teacher Workload' },
  { key: 'forecast', label: 'Fee Forecast' },
  { key: 'campuses', label: 'Cross-Campus' },
  { key: 'attendance-risk', label: 'Attendance-Risk Alerts' },
  { key: 'builder', label: 'Report Builder' },
  { key: 'export', label: 'Data Export' },
  { key: 'scheduled', label: 'Scheduled Reports' },
];

function StrategicReportsPage() {
  // Deep-linkable via ?tab=<key> — e.g. the Executive Dashboard's "Fee
  // Overdue Balance" tile links straight to ?tab=forecast.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(TABS.some((t) => t.key === requestedTab) ? requestedTab : 'retention');
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    if (requestedTab && TABS.some((t) => t.key === requestedTab) && requestedTab !== tab) {
      setTab(requestedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  const changeTab = (key) => {
    setTab(key);
    setSearchParams({ tab: key });
  };

  useEffect(() => {
    schoolsService.listAllTerms().then((data) => setTerms(data?.items || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="page strategic-reports-page">
        <div className="page-header">
          <div>
            <h1>Strategic Reports</h1>
            <p>Retention, enrollment trends, teacher workload, fee forecasting, cross-campus comparison, proactive alerts, and a custom report builder</p>
          </div>
          <BoardPackButton terms={terms} />
        </div>

        <div className="srp-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => changeTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'retention' && <RetentionEnrollmentTab />}
        {tab === 'workload' && <WorkloadTab terms={terms} />}
        {tab === 'forecast' && <ForecastTab />}
        {tab === 'campuses' && <CampusesTab terms={terms} />}
        {tab === 'attendance-risk' && <AttendanceRiskTab />}
        {tab === 'builder' && <ReportBuilderTab />}
        {tab === 'export' && <ExportTab />}
        {tab === 'scheduled' && <ScheduledReportsTab />}
      </div>
    </Layout>
  );
}

/* ---------------------------------------------------------------- */
/* Board Report Pack (PDF download)                                  */
/* ---------------------------------------------------------------- */

const monthAgoIso = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};
const todayIso = () => new Date().toISOString().split('T')[0];

function BoardPackButton({ terms }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    const currentTerm = terms.find((t) => t.is_current) || terms[0];
    if (!currentTerm) {
      toast.error('Set up an academic term before generating a board pack');
      return;
    }
    setDownloading(true);
    try {
      const response = await strategicReportsService.getBoardPack({
        start_date: monthAgoIso(),
        end_date: todayIso(),
        academic_term_id: currentTerm.id,
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `board-pack-${todayIso()}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Board pack downloaded');
    } catch (error) {
      toast.error('Failed to generate board pack: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button className="btn btn-secondary" onClick={download} disabled={downloading}>
      <DownloadSimple size={16} /> {downloading ? 'Generating...' : 'Download Board Pack'}
    </button>
  );
}

function KeyValueTable({ data, keyLabel = 'Key', valueLabel = 'Count' }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <p className="empty">No data</p>;
  return (
    <div className="srp-table">
      <table>
        <thead><tr><th>{keyLabel}</th><th>{valueLabel}</th></tr></thead>
        <tbody>
          {entries.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Retention & Enrollment                                            */
/* ---------------------------------------------------------------- */

function RetentionEnrollmentTab() {
  const [months, setMonths] = useState(12);
  const [retention, setRetention] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([
        strategicReportsService.getRetention({ months }),
        strategicReportsService.getEnrollmentTrends({ months }),
      ]);
      setRetention(r);
      setEnrollment(e);
    } catch (error) {
      toast.error('Failed to load retention/enrollment data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="srp-panel">
      <div className="form-group srp-inline-filter">
        <label>Window (months)</label>
        <input type="number" min="1" max="36" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 12)} />
      </div>

      <div className="srp-grid">
        <div className="srp-card">
          <h3>Retention</h3>
          <div className="srp-stat-row">
            <div className="srp-stat"><span className="srp-stat-value">{retention?.retention_rate}%</span><span className="srp-stat-label">Retention Rate</span></div>
            <div className="srp-stat"><span className="srp-stat-value">{retention?.total_students}</span><span className="srp-stat-label">Total Students</span></div>
            <div className="srp-stat"><span className="srp-stat-value">{retention?.dropout_count}</span><span className="srp-stat-label">Dropouts ({months}mo)</span></div>
          </div>
          <h4>By Status</h4>
          <KeyValueTable data={retention?.by_status} keyLabel="Status" />
          <h4>Dropouts by Reason</h4>
          <KeyValueTable data={retention?.dropouts_by_reason} keyLabel="Reason" />
          <h4>Dropouts by Month</h4>
          <KeyValueTable data={retention?.dropouts_by_month} keyLabel="Month" />
        </div>

        <div className="srp-card">
          <h3>Enrollment Trends</h3>
          <div className="srp-stat-row">
            <div className="srp-stat"><span className="srp-stat-value">{enrollment?.new_enrollments}</span><span className="srp-stat-label">New Enrollments</span></div>
          </div>
          <h4>By Month</h4>
          <KeyValueTable data={enrollment?.by_month} keyLabel="Month" />
          <h4>By Campus</h4>
          <KeyValueTable data={enrollment?.by_campus} keyLabel="Campus" />
          <h4>By Admission Type</h4>
          <KeyValueTable data={enrollment?.by_admission_type} keyLabel="Type" />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Teacher Workload                                                   */
/* ---------------------------------------------------------------- */

function WorkloadTab({ terms }) {
  const [termId, setTermId] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!termId && terms.length) setTermId(terms.find((t) => t.is_current)?.id || terms[0].id);
  }, [terms, termId]);

  const load = useCallback(async () => {
    if (!termId) return;
    setLoading(true);
    try {
      const data = await strategicReportsService.getTeacherWorkload(termId);
      setTeachers(data.teachers || []);
    } catch (error) {
      toast.error('Failed to load teacher workload: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [termId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="srp-panel">
      <div className="form-group srp-inline-filter">
        <label>Academic Term</label>
        <select value={termId} onChange={(e) => setTermId(e.target.value)}>
          {terms.map((t) => <option key={t.id} value={t.id}>{t.academic_year} — {t.term}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : teachers.length === 0 ? (
        <div className="empty-state"><p>No timetable entries found for this term</p></div>
      ) : (
        <div className="srp-table">
          <table>
            <thead><tr><th>Teacher</th><th>Periods/Week</th><th>Classes</th><th>Subjects</th><th>Students Taught</th><th>Grades Recorded</th></tr></thead>
            <tbody>
              {teachers.map((t) => (
                <tr key={t.teacher_id}>
                  <td>{t.teacher_name}</td><td>{t.periods_per_week}</td><td>{t.classes_taught}</td>
                  <td>{t.subjects_taught}</td><td>{t.students_taught}</td><td>{t.grades_recorded}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Fee Forecast                                                       */
/* ---------------------------------------------------------------- */

function ForecastTab() {
  const [months, setMonths] = useState(3);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setForecast(await strategicReportsService.getFeeForecast({ months }));
    } catch (error) {
      toast.error('Failed to load fee forecast: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="srp-panel">
      <div className="form-group srp-inline-filter">
        <label>Horizon (months)</label>
        <input type="number" min="1" max="12" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 3)} />
      </div>

      <div className="srp-card">
        <div className="srp-stat-row">
          <div className="srp-stat"><span className="srp-stat-value">{((forecast?.historical_on_time_collection_rate || 0) * 100).toFixed(0)}%</span><span className="srp-stat-label">Historical On-Time Rate</span></div>
          <div className="srp-stat"><span className="srp-stat-value">GHS {forecast?.current_overdue_balance?.toLocaleString()}</span><span className="srp-stat-label">Current Overdue</span></div>
          <div className="srp-stat"><span className="srp-stat-value">GHS {forecast?.total_projected_collected?.toLocaleString()}</span><span className="srp-stat-label">Projected Collection</span></div>
        </div>

        <h4>By Month</h4>
        <div className="srp-table">
          <table>
            <thead><tr><th>Month</th><th>Billed</th><th>Projected Collected</th></tr></thead>
            <tbody>
              {Object.entries(forecast?.by_month || {}).map(([month, v]) => (
                <tr key={month}><td>{month}</td><td>GHS {v.billed.toLocaleString()}</td><td>GHS {v.projected_collected.toLocaleString()}</td></tr>
              ))}
              {Object.keys(forecast?.by_month || {}).length === 0 && <tr><td colSpan={3} className="empty">No upcoming installments in this window</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Cross-Campus Comparison                                            */
/* ---------------------------------------------------------------- */

function CampusesTab({ terms }) {
  const [termId, setTermId] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!termId && terms.length) setTermId(terms.find((t) => t.is_current)?.id || terms[0].id);
  }, [terms, termId]);

  const load = useCallback(async () => {
    if (!termId) return;
    setLoading(true);
    try {
      const data = await strategicReportsService.getCampusComparison(termId);
      setCampuses(data.campuses || []);
    } catch (error) {
      toast.error('Failed to load campus comparison: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [termId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="srp-panel">
      <div className="form-group srp-inline-filter">
        <label>Academic Term</label>
        <select value={termId} onChange={(e) => setTermId(e.target.value)}>
          {terms.map((t) => <option key={t.id} value={t.id}>{t.academic_year} — {t.term}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : campuses.length === 0 ? (
        <div className="empty-state"><p>No campuses set up for this school yet</p></div>
      ) : (
        <div className="srp-table">
          <table>
            <thead><tr><th>Campus</th><th>Students</th><th>Staff</th><th>Avg GPA</th><th>Attendance</th><th>Fee Collection</th></tr></thead>
            <tbody>
              {campuses.map((c) => (
                <tr key={c.campus_id}>
                  <td>{c.campus_name}</td><td>{c.student_count}</td><td>{c.staff_count}</td>
                  <td>{c.average_gpa ?? '—'}</td><td>{c.attendance_rate != null ? `${c.attendance_rate}%` : '—'}</td>
                  <td>{c.fee_collection_rate != null ? `${c.fee_collection_rate}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Attendance-Risk Alerts                                             */
/* ---------------------------------------------------------------- */

function AttendanceRiskTab() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([strategicReportsService.getAttendanceRiskSettings(), strategicReportsService.getAttendanceRiskHistory()]);
      setSettings(s);
      setHistory(h || []);
    } catch (error) {
      toast.error('Failed to load attendance-risk data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      setSettings(await strategicReportsService.updateAttendanceRiskSettings(settings));
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const result = await strategicReportsService.runAttendanceRiskNow();
      toast.success(`${result.alerts_sent} alert(s) sent — ${result.students_flagged} student(s) flagged`);
      load();
    } catch (error) {
      toast.error('Failed to run sweep: ' + error.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading || !settings) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="srp-panel srp-two-col">
      <div className="srp-card">
        <h3>Settings</h3>
        <div className="form-group">
          <label><input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} /> Enabled</label>
        </div>
        <div className="form-group"><label>Alert Threshold (%)</label><input type="number" min="0" max="100" value={settings.threshold_percent} onChange={(e) => setSettings({ ...settings, threshold_percent: parseFloat(e.target.value) || 0 })} /></div>
        <div className="form-group"><label>Lookback Window (days)</label><input type="number" min="1" value={settings.lookback_days} onChange={(e) => setSettings({ ...settings, lookback_days: parseInt(e.target.value, 10) || 1 })} /></div>
        <div className="form-group"><label>Cooldown Between Alerts (days)</label><input type="number" min="1" value={settings.cooldown_days} onChange={(e) => setSettings({ ...settings, cooldown_days: parseInt(e.target.value, 10) || 1 })} /></div>
        <div className="srp-row-actions">
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Settings'}</button>
          <button className="btn btn-secondary" disabled={running} onClick={runNow}>{running ? 'Running...' : 'Run Now'}</button>
        </div>
      </div>

      <div className="srp-card">
        <h3>Alert History</h3>
        {history.length === 0 ? <p className="empty">No alerts sent yet</p> : (
          <div className="srp-table">
            <table>
              <thead><tr><th>Student</th><th>Rate</th><th>Channel</th><th>Status</th><th>Sent</th></tr></thead>
              <tbody>
                {history.map((a) => (
                  <tr key={a.id}>
                    <td>{a.student_id}</td><td>{a.attendance_rate.toFixed(1)}%</td><td>{a.channel}</td><td>{a.status}</td>
                    <td>{a.sent_at ? new Date(a.sent_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Custom Report Builder                                              */
/* ---------------------------------------------------------------- */

function ReportBuilderTab() {
  const [fieldMap, setFieldMap] = useState({});
  const [exportColumns, setExportColumns] = useState({});
  const [source, setSource] = useState('students');
  const [groupBy, setGroupBy] = useState('');
  const [aggregation, setAggregation] = useState('count');
  const [measureField, setMeasureField] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [definitions, setDefinitions] = useState([]);
  const [reportName, setReportName] = useState('');
  const [drillingDown, setDrillingDown] = useState(null);

  const loadDefinitions = useCallback(async () => {
    try {
      setDefinitions(await strategicReportsService.listDefinitions());
    } catch (error) {
      toast.error('Failed to load saved reports: ' + error.message);
    }
  }, []);

  useEffect(() => {
    strategicReportsService.getFieldMap().then((data) => {
      setFieldMap(data);
      const first = Object.keys(data)[0];
      if (first) setGroupBy(data[first].group_by[0] || '');
    }).catch(() => {});
    strategicReportsService.getExportColumns().then(setExportColumns).catch(() => {});
    loadDefinitions();
  }, [loadDefinitions]);

  // A result row can be drilled into a row-level CSV export (source/columns
  // scoped to the clicked group) only when that source supports row-level
  // export at all — e.g. "fees" doesn't have export columns configured.
  const drillDownColumns = exportColumns[source] || [];
  const canDrillDown = drillDownColumns.length > 0;

  const drillDown = async (row) => {
    if (!canDrillDown) {
      toast.error(`Row-level export isn't available for "${source}" yet`);
      return;
    }
    setDrillingDown(row.group_key);
    try {
      const response = await strategicReportsService.getExportCsv(source, drillDownColumns, { [groupBy]: row.group_key });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${source}_${String(row.group).replace(/\s+/g, '_')}_export.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Row-level export downloaded');
    } catch (error) {
      toast.error('Failed to export rows: ' + error.message);
    } finally {
      setDrillingDown(null);
    }
  };

  const spec = fieldMap[source] || { group_by: [], measures: [] };

  const run = async () => {
    if (!groupBy) { toast.error('Choose a Group By field'); return; }
    setRunning(true);
    try {
      setResult(await strategicReportsService.runAdHocReport({ source, group_by: groupBy, aggregation, measure_field: aggregation === 'count' ? null : measureField }));
    } catch (error) {
      toast.error('Failed to run report: ' + error.message);
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    if (!reportName.trim()) { toast.error('Give the report a name'); return; }
    try {
      await strategicReportsService.createDefinition({ name: reportName, source, group_by: groupBy, aggregation, measure_field: aggregation === 'count' ? null : measureField });
      toast.success('Report saved');
      setReportName('');
      loadDefinitions();
    } catch (error) {
      toast.error('Failed to save report: ' + error.message);
    }
  };

  const runSaved = async (id) => {
    try {
      setResult(await strategicReportsService.runDefinition(id));
    } catch (error) {
      toast.error('Failed to run report: ' + error.message);
    }
  };

  const removeSaved = async (id) => {
    try {
      await strategicReportsService.deleteDefinition(id);
      toast.success('Report deleted');
      loadDefinitions();
    } catch (error) {
      toast.error('Failed to delete report: ' + error.message);
    }
  };

  return (
    <div className="srp-panel srp-two-col">
      <div className="srp-card">
        <h3>Build a Report</h3>
        <div className="form-group">
          <label>Source</label>
          <select value={source} onChange={(e) => { setSource(e.target.value); setGroupBy((fieldMap[e.target.value]?.group_by || [])[0] || ''); setMeasureField(''); }}>
            {Object.keys(fieldMap).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Group By</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            {spec.group_by.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Aggregation</label>
          <select value={aggregation} onChange={(e) => setAggregation(e.target.value)}>
            <option value="count">Count</option>
            <option value="avg">Average</option>
            <option value="sum">Sum</option>
          </select>
        </div>
        {aggregation !== 'count' && (
          <div className="form-group">
            <label>Measure Field</label>
            <select value={measureField} onChange={(e) => setMeasureField(e.target.value)}>
              <option value="">Select…</option>
              {spec.measures.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
        <div className="srp-row-actions">
          <button className="btn btn-primary" disabled={running} onClick={run}>{running ? 'Running...' : 'Run'}</button>
        </div>

        <div className="form-group srp-mt">
          <label>Save this shape as</label>
          <input value={reportName} onChange={(e) => setReportName(e.target.value)} placeholder="Report name" />
        </div>
        <button className="btn btn-secondary" onClick={save}>Save Report</button>

        <h4 className="srp-mt">Saved Reports</h4>
        {definitions.length === 0 ? <p className="empty">None yet</p> : (
          <ul className="srp-list">
            {definitions.map((d) => (
              <li key={d.id}>
                <span>{d.name} <em>({d.source} / {d.group_by} / {d.aggregation})</em></span>
                <span className="srp-row-actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => runSaved(d.id)}>Run</button>
                  <button className="btn btn-sm btn-danger" onClick={() => removeSaved(d.id)}>Delete</button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="srp-card">
        <h3>Results</h3>
        {!result ? <p className="empty">Run a report to see results</p> : (
          <div className="srp-table">
            {canDrillDown && result.rows.length > 0 && <p className="row-note">Click a row to download its underlying records as CSV</p>}
            <table>
              <thead><tr><th>{result.group_by}</th><th>{result.aggregation}{result.measure_field ? ` (${result.measure_field})` : ''}</th></tr></thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr
                    key={i}
                    className={canDrillDown ? 'srp-row-clickable' : ''}
                    onClick={canDrillDown ? () => drillDown(r) : undefined}
                  >
                    <td>{r.group}</td>
                    <td>{drillingDown === r.group_key ? 'Exporting…' : r.value}</td>
                  </tr>
                ))}
                {result.rows.length === 0 && <tr><td colSpan={2} className="empty">No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Data Export (government/EMIS-style row-level CSV)                 */
/* ---------------------------------------------------------------- */

function ExportTab() {
  const [exportColumns, setExportColumns] = useState({});
  const [fieldMap, setFieldMap] = useState({});
  const [source, setSource] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([strategicReportsService.getExportColumns(), strategicReportsService.getFieldMap()])
      .then(([cols, fm]) => {
        setExportColumns(cols || {});
        setFieldMap(fm || {});
        const first = Object.keys(cols || {})[0];
        if (first) setSource(first);
      })
      .catch((error) => toast.error('Failed to load export options: ' + error.message))
      .finally(() => setLoading(false));
  }, []);

  const columns = exportColumns[source] || [];
  const filterKeys = fieldMap[source]?.filters || [];

  const handleSourceChange = (s) => {
    setSource(s);
    setSelectedColumns([]);
    setFilterValues({});
  };

  const toggleColumn = (col) => {
    setSelectedColumns((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleDownload = async () => {
    if (selectedColumns.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    setDownloading(true);
    try {
      const filters = {};
      Object.entries(filterValues).forEach(([k, v]) => {
        if (v && v.trim()) filters[k] = v.trim();
      });
      const response = await strategicReportsService.getExportCsv(
        source,
        selectedColumns,
        Object.keys(filters).length ? filters : undefined
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${source}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to download export: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="srp-panel srp-two-col">
      <div className="srp-card">
        <h3>Configure Export</h3>
        <div className="form-group">
          <label>Source</label>
          <select value={source} onChange={(e) => handleSourceChange(e.target.value)}>
            {Object.keys(exportColumns).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Columns</label>
          <div className="srp-row-actions srp-mb-sm">
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSelectedColumns(columns)}>Select All</button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSelectedColumns([])}>Select None</button>
          </div>
          <div className="srp-checkbox-grid">
            {columns.map((col) => (
              <label key={col} className="srp-checkbox-item">
                <input type="checkbox" checked={selectedColumns.includes(col)} onChange={() => toggleColumn(col)} />
                {col}
              </label>
            ))}
            {columns.length === 0 && <p className="empty">No columns available for this source</p>}
          </div>
        </div>

        {filterKeys.length > 0 && (
          <div className="form-group">
            <label>Filters (optional)</label>
            {filterKeys.map((key) => (
              <div key={key} className="srp-filter-row">
                <label className="srp-filter-label">{key}</label>
                <input
                  type="text"
                  value={filterValues[key] || ''}
                  onChange={(e) => handleFilterChange(key, e.target.value)}
                  placeholder={`Filter by ${key}`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="srp-row-actions">
          <button className="btn btn-primary" disabled={downloading || selectedColumns.length === 0} onClick={handleDownload}>
            {downloading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
      </div>

      <div className="srp-card">
        <h3>About this export</h3>
        <p className="empty">
          Row-level extract of raw {source || 'record'} data — one row per record, in the government/EMIS
          submission shape. Pick a source, choose which columns to include (and their order), optionally
          narrow the result with filters, then download the CSV.
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Scheduled Reports                                                  */
/* ---------------------------------------------------------------- */

const CANNED_KEYS = ['attendance', 'fee-collection', 'academic-performance', 'enrollment'];

function ScheduledReportsTab() {
  const [scheduled, setScheduled] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', kind: 'saved', saved_report_definition_id: '', canned_report_key: 'attendance', frequency: 'weekly', recipients: '' });
  const [submitting, setSubmitting] = useState(false);
  const [runningDue, setRunningDue] = useState(false);
  const [runsFor, setRunsFor] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([strategicReportsService.listScheduledReports(), strategicReportsService.listDefinitions()]);
      setScheduled(s || []);
      setDefinitions(d || []);
    } catch (error) {
      toast.error('Failed to load scheduled reports: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    const recipients = form.recipients.split(',').map((r) => r.trim()).filter(Boolean);
    if (!form.name.trim() || recipients.length === 0) { toast.error('Name and at least one recipient are required'); return; }
    setSubmitting(true);
    try {
      await strategicReportsService.createScheduledReport({
        name: form.name,
        saved_report_definition_id: form.kind === 'saved' ? form.saved_report_definition_id : null,
        canned_report_key: form.kind === 'canned' ? form.canned_report_key : null,
        frequency: form.frequency,
        recipients,
      });
      toast.success('Scheduled report created');
      setShowCreate(false);
      setForm({ name: '', kind: 'saved', saved_report_definition_id: '', canned_report_key: 'attendance', frequency: 'weekly', recipients: '' });
      load();
    } catch (error) {
      toast.error('Failed to create scheduled report: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnabled = async (item) => {
    try {
      await strategicReportsService.updateScheduledReport(item.id, { enabled: !item.enabled });
      load();
    } catch (error) {
      toast.error('Failed to update: ' + error.message);
    }
  };

  const remove = async (id) => {
    try {
      await strategicReportsService.deleteScheduledReport(id);
      toast.success('Deleted');
      load();
    } catch (error) {
      toast.error('Failed to delete: ' + error.message);
    }
  };

  const runDueNow = async () => {
    setRunningDue(true);
    try {
      const result = await strategicReportsService.runDueScheduledReports();
      toast.success(`${result.reports_sent} of ${result.reports_checked} due report(s) sent`);
      load();
    } catch (error) {
      toast.error('Failed to run due reports: ' + error.message);
    } finally {
      setRunningDue(false);
    }
  };

  const viewRuns = async (id) => {
    setRunsFor(id);
    try {
      setRuns(await strategicReportsService.listScheduledReportRuns(id));
    } catch (error) {
      toast.error('Failed to load run history: ' + error.message);
    }
  };

  return (
    <div className="srp-panel">
      <div className="srp-row-actions">
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>New Scheduled Report</button>
        <button className="btn btn-secondary" disabled={runningDue} onClick={runDueNow}>{runningDue ? 'Running...' : 'Run Due Now'}</button>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : scheduled.length === 0 ? (
        <div className="empty-state"><p>No scheduled reports yet</p></div>
      ) : (
        <div className="srp-table">
          <table>
            <thead><tr><th>Name</th><th>Source</th><th>Frequency</th><th>Recipients</th><th>Enabled</th><th>Last Run</th><th>Actions</th></tr></thead>
            <tbody>
              {scheduled.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.saved_report_definition_id ? 'Custom' : s.canned_report_key}</td>
                  <td>{s.frequency}</td>
                  <td>{s.recipients.join(', ')}</td>
                  <td>{s.enabled ? 'Yes' : 'No'}</td>
                  <td>{s.last_run_at ? new Date(s.last_run_at).toLocaleString() : '—'}</td>
                  <td className="srp-row-actions">
                    <button className="btn btn-sm btn-secondary" onClick={() => toggleEnabled(s)}>{s.enabled ? 'Disable' : 'Enable'}</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => viewRuns(s.id)}>History</button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>New Scheduled Report</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <div className="form-group"><label>Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
              <div className="form-group">
                <label>Report Source</label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="saved">Custom (from Report Builder)</option>
                  <option value="canned">Canned Report</option>
                </select>
              </div>
              {form.kind === 'saved' ? (
                <div className="form-group">
                  <label>Saved Report</label>
                  <select value={form.saved_report_definition_id} onChange={(e) => setForm({ ...form, saved_report_definition_id: e.target.value })} required>
                    <option value="">Select…</option>
                    {definitions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Canned Report</label>
                  <select value={form.canned_report_key} onChange={(e) => setForm({ ...form, canned_report_key: e.target.value })}>
                    {CANNED_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Frequency</label>
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                  <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="form-group"><label>Recipients (comma-separated emails) *</label><input value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="admin@school.com, head@school.com" required /></div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {runsFor && (
        <div className="modal-overlay" onClick={() => setRunsFor(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Run History</h2><button className="btn-close" onClick={() => setRunsFor(null)}>×</button></div>
            {runs.length === 0 ? <p className="empty">No runs yet</p> : (
              <div className="srp-table">
                <table>
                  <thead><tr><th>Ran At</th><th>Status</th><th>Recipients</th><th>Error</th></tr></thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.id}><td>{new Date(r.run_at).toLocaleString()}</td><td>{r.status}</td><td>{r.recipients_sent}</td><td>{r.error_message || '—'}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategicReportsPage;
